/* Copyright (c) 2020-present CloudIO Inc */

import {
  AnyItem,
  DragProps,
  ItemData,
  ItemID,
  ItemType,
  Page,
} from 'componentregistry';
import {
  atom,
  Key,
  Path,
  useStateXAtomValue,
  useStateXValue,
} from '@cloudio-saas/statex';
import Logger from 'utils/Logger';
import { PivotGridConfiguration } from '@cloudio-saas/datasource-types';
import { DefaultPivotGridConfiguration } from '../custom/PivotGrid/PivotGridConfiguration';
import { usePlatform } from 'platform/PlatformHooks';
import { isDeveloper, isViewOnlyDeveloper } from 'root/useUser';

function namedPath(
  name: string,
  appUid?: Key,
  pageId?: Key,
  itemId?: Key,
  key?: Key,
  subkey?: Key,
): Path {
  if (appUid && pageId && itemId && key && subkey) {
    return ['pageStore', name, appUid, pageId, itemId, key, subkey];
  }
  if (appUid && pageId && itemId && key) {
    return ['pageStore', name, appUid, pageId, itemId, key];
  }
  if (appUid && pageId && itemId) {
    return ['pageStore', name, appUid, pageId, itemId];
  }
  if (appUid && pageId) {
    return ['pageStore', name, appUid, pageId];
  }
  if (appUid) {
    return ['pageStore', name, appUid, ':pageId'];
  }
  return ['pageStore', name, ':appUid', ':pageId'];
}

export function designPath({
  appUid,
  pageId,
  itemId,
  key,
  subkey,
}: {
  appUid?: Key;
  pageId?: Key;
  itemId?: Key;
  key?: Key;
  subkey?: Key;
}): Path {
  return namedPath('design', appUid, pageId, itemId, key, subkey);
}

export function runtimePath({
  appUid,
  pageId,
  itemId,
  key,
  subkey,
}: {
  appUid?: Key;
  pageId?: Key;
  itemId?: Key;
  key?: Key;
  subkey?: Key;
}): Path {
  return namedPath('runtime', appUid, pageId, itemId, key, subkey);
}

export function formulaPath({
  appUid,
  pageId,
  itemId,
  key,
  subkey,
}: {
  appUid?: Key;
  pageId?: Key;
  itemId?: Key;
  key?: Key;
  subkey?: Key;
}): Path {
  return namedPath('formula', appUid, pageId, itemId, key, subkey);
}

export const contextAppIdAtom = atom({
  path: ['pageStore', 'context', 'appUid'],
  defaultValue: 'cloudio',
});

export function useAppID() {
  return useStateXAtomValue(contextAppIdAtom);
}

export const pageAtom = atom<Page | undefined>({
  path: designPath({}),
  defaultValue: undefined,
});

export const itemAtom = atom<AnyItem | undefined>({
  path: [...designPath({}), ':itemId'],
  defaultValue: undefined,
});

export const itemPropsAtom = atom<unknown | undefined>({
  path: [...designPath({}), ':itemId', 'props'],
  defaultValue: undefined,
});

export const itemPropPath = [...designPath({}), ':itemId', 'props', ':prop'];

export const formulaPropPath = [
  ...formulaPath({}),
  ':itemId',
  'props',
  ':prop',
];

export const itemTypeAtom = atom<ItemType>({
  path: [...designPath({}), ':itemId', 'type'],
  defaultValue: 'Page',
});

export const childrenAtom = atom<ItemID[]>({
  path: [...designPath({}), ':itemId', 'children'],
  defaultValue: [],
});

export const childrenRTAtom = atom<ItemID[]>({
  path: [...runtimePath({}), ':itemId', 'children'],
  defaultValue: [],
});

export const formulaChildrenRTAtom = atom<ItemID[]>({
  path: [...formulaPath({}), ':itemId', 'children'],
  defaultValue: [],
});

export const smartSearchChildrenRTAtom = atom<ItemID[]>({
  path: [...runtimePath({}), ':itemId', 'smartChildren'],
  defaultValue: [],
});

export const pivotConfigAtom = atom<PivotGridConfiguration>({
  path: [...runtimePath({}), ':itemId', 'configuration'],
  defaultValue: DefaultPivotGridConfiguration,
});

export const formulaItemDataAtom = atom<AnyItem | undefined>({
  path: [...formulaPath({}), ':itemId'],
  defaultValue: undefined,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const runtimeItemAtom = atom<Partial<ItemData<any>>>({
  path: [...runtimePath({}), ':itemId'],
  defaultValue: {},
});

export const runtimeItemPropAtom = atom<unknown>({
  path: [...runtimePath({}), ':itemId', ':prop'],
  defaultValue: undefined,
});

export const formulaItemPropsAtom = atom<unknown>({
  path: [...formulaPath({}), ':itemId', 'props', ':prop'],
  defaultValue: undefined,
});

export const developerModePreferenceAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'developerMode'],
  defaultValue: false,
});

export const debugLogPreferenceAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'clientDebugLog'],
  defaultValue: false,
  onChange: ({ value }) => {
    Logger.setEnabled(value);
  },
});

export const hideTipsAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'hideTips'],
  defaultValue: false,
});

export const closeSidebarOnPageNavigationAtom = atom({
  path: [
    'db',
    'IoUserSettings',
    ':userName',
    'md',
    'closeSidebarOnPageNavigation',
  ],
  defaultValue: true,
});

export const autoPageRefreshAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'autoPageRefresh'],
  defaultValue: true,
});

export const disableMessageCompressionAtom = atom({
  path: [
    'db',
    'IoUserSettings',
    ':userName',
    'md',
    'disableMessageCompression',
  ],
  defaultValue: false,
});

export const scrollToEndOnServerLogAtom = atom({
  path: ['transient', 'scrollToEndOnServerLog'],
  defaultValue: true,
});

export const pulseHiddenStateAtom = atom({
  path: [
    'db',
    'IoUserSettings',
    ':userName',
    'md',
    'pulse',
    ':pageId',
    ':itemId',
  ],
  defaultValue: false,
});

export const floatingSidebarsAtom = atom({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'floatingSidebars'],
  defaultValue: false,
});

export const developerModeAtom = atom({
  path: ['transient', 'designer', ':appUid', 'developerMode'],
  defaultValue: false,
  urlParam: true,
});

export function useDeveloperMode(appUid: string, pageId: string): boolean {
  const platform = usePlatform();
  const pageAppUid = useStateXValue(
    ['db', 'IoPages', pageId || 'x', 'appUid'],
    '',
  );
  const devMode = useStateXAtomValue(developerModeAtom, { params: { appUid } });
  // pageAppUid is empty when the page is not yet loaded
  // in this case we assume that the page is in the current app
  // otherwise when the browser is refreshed while on designer
  // the page is getting unmounted after the initial render
  // causing the localDataStore not to be initialized (due to mounted.current being false)
  if (
    pageId &&
    pageAppUid &&
    pageId !== `${appUid}.home` &&
    pageAppUid !== appUid
  ) {
    return false;
  }
  return devMode && isDeveloper(platform, appUid);
}

export function useViewMode(appUid: string, pageId: string): boolean {
  const platform = usePlatform();
  const pageAppUid = useStateXValue(
    ['db', 'IoPages', pageId || 'x', 'appUid'],
    '',
  );
  const devMode = useStateXAtomValue(developerModeAtom, { params: { appUid } });
  // pageAppUid is empty when the page is not yet loaded
  // in this case we assume that the page is in the current app
  // otherwise when the browser is refreshed while on designer
  // the page is getting unmounted after the initial render
  // causing the localDataStore not to be initialized (due to mounted.current being false)
  if (
    pageId &&
    pageAppUid &&
    pageId !== `${appUid}.home` &&
    pageAppUid !== appUid
  ) {
    return false;
  }
  return devMode && isViewOnlyDeveloper(platform, appUid);
}

export const embeddedPagesAtom = atom<ItemID[]>({
  path: ['pageStore', 'embedded'],
  defaultValue: [],
});

export const showHiddenItemsAtom = atom({
  path: ['transient', 'designer', 'showHiddenItems'],
  defaultValue: false,
  urlParam: true,
});

export const draggingAtom = atom({
  path: ['transient', 'designer', 'dragging'],
  defaultValue: false,
});

export const dragItemAtom = atom<ItemID | undefined>({
  path: ['transient', 'designer', 'dragItemId'],
  defaultValue: undefined,
});

export const dragItemPropsAtom = atom<DragProps | undefined>({
  path: ['transient', 'designer', 'dragItemProps'],
  defaultValue: undefined,
});

export const dragTipAtom = atom({
  path: ['transient', 'designer', 'dragTip'],
  defaultValue: '',
});

export const hideHeaderAtom = atom({
  path: ['transient', 'designer', 'hideHeader'],
  defaultValue: false,
  urlParam: true,
});

export const hideSidebarAtom = atom({
  path: ['transient', 'designer', 'hideSidebar'],
  defaultValue: false,
  urlParam: true,
});

export const hideDevToolbarAtom = atom({
  path: ['transient', 'designer', 'hideDevToolbar'],
  defaultValue: false,
  urlParam: true,
});

export const copyItemAtom = atom<
  { itemId: string; pageId: string; type: ItemType } | undefined
>({
  path: ['transient', 'designer', 'copyItem'],
  defaultValue: undefined,
});

export const dragOverItemIdsAtom = atom<string[]>({
  path: ['transient', 'designer', 'dragOverItemIds'],
  defaultValue: [],
});

export const dragSourceOffsetAtom = atom({
  path: ['transient', 'designer', 'dragSourceOffset'],
  defaultValue: { x: 0, y: 0 },
});

export const activeItemIdAtom = atom({
  path: [
    'pageStore',
    'design',
    'transient',
    ':treename',
    'activeItemId',
    ':appUid',
    ':pageId',
  ],
  defaultValue: '',
  urlParam: true,
});

export const hideDefaultPropsAtom = atom({
  path: ['pageStore', 'design', 'transient', 'hideDefaultProps'],
  defaultValue: false,
});

export const lockPropsAtom = atom({
  path: ['pageStore', 'design', 'transient', 'lockProps'],
  defaultValue: false,
});

export const activeItemIdsAtom = atom<string[]>({
  path: [
    'pageStore',
    'design',
    'transient',
    'activeItemIds',
    ':appUid',
    ':pageId',
  ],
  defaultValue: [],
  urlParam: true,
});

export const hmrAtom = atom({
  path: ['_hmr_'],
  defaultValue: 1,
});

interface Hover {
  itemId: string;
  pageId: string;
  rowIndex: number;
}

export const hoverItemsAtom = atom<Hover[]>({
  path: ['pageStore', 'design', 'transient', 'hoverItems'],
  defaultValue: [],
});

export const activeItemPathAtom = atom<string[]>({
  path: [
    'pageStore',
    'design',
    'transient',
    'activeItemPath',
    ':appUid',
    ':pageId',
  ],
  defaultValue: [],
  urlParam: true,
});

export const activeItemRefPath = ['transient', 'designer', 'activeItemRef'];

export const activeItemRefCounterAtom = atom({
  path: ['transient', 'designer', 'activeItemRefCounter'],
  defaultValue: 0,
});

// export const dragHoverIdAtom = atom({
//   path: ['transient', 'designer', 'dragHoverId'],
//   defaultValue: '',
// });

export const prevHoverIdAtom = atom({
  path: ['transient', 'designer', 'prevHoverId'],
  defaultValue: '',
});

export const clipboardIdAtom = atom({
  path: ['transient', 'designer', 'clipboardId'],
  defaultValue: '',
});

export const prevTimeAtom = atom({
  path: ['transient', 'designer', 'prevTime'],
  defaultValue: 0,
});

export const prevXYCoordAtom = atom({
  path: ['transient', 'designer', 'prevXYCoord'],
  defaultValue: { x: -1, y: -1 },
});

export const initialXYCoordAtom = atom({
  path: ['transient', 'designer', 'initXYCoord'],
  defaultValue: { x: -1, y: -1 },
});
